import { Modal } from "components/designV2/modal/modal";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Text } from "components/designV2/text/text";
import { TextArea } from "components/designV2/textarea/textarea";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import {
  AddMoreShiftsRow,
  DescriptionFieldWrap,
  FormFlow,
  ModalBody,
  SectionCard,
  SectionHeaderRow,
  SectionMetrics,
} from "../createGigModal.styled";
import { ShiftForm } from "../sections/shiftForm.component";
import { ShiftList } from "../sections/shiftList.component";
import { useGigFormModalLogic } from "./gigFormModal.logic";
import { GIG_DESCRIPTION_MAX_LENGTH } from "../logic/gigForm.validation.logic";
import type { GigFormModalProps, RecruitFormData } from "./gigFormModal.types";
import { GigFormTabs } from "./gigFormTabs.component";
import { GigFormTabPanels } from "./gigFormTabPanels.component";

export type { FormData, RecruitFormData } from "./gigFormModal.types";

export const GigFormModal: React.FC<GigFormModalProps> = (props) => {
  const { t } = useTranslation();
  const {
    canRecruit,
    changeTo,
    closeModal,
    formData,
    handleChangeTab,
    handleRequestClose,
    handleSubmitGigForm,
    havePublicModule,
    haveTeamModule,
    isLoading,
    isAddingMoreShifts,
    isPublicRecruit,
    hasInvalidShiftTimes,
    totalHours,
    totalMinutes,
    visibleForValue,
    recruitFormData,
    requestTabChange,
    selectedWorkers,
    overlapping,
    setFormData,
    setRecruitFormData,
    setSelectedWorkers,
    setShowAlert,
    setShowChangeTabAlert,
    setIsAddingMoreShifts,
    shiftListOpen,
    showAlert,
    showChangeTabAlert,
    tab,
    isFormValid,
    createDisabledReasonKeys,
  } = useGigFormModalLogic(props);

  const createDisabledReasons = createDisabledReasonKeys.map((key) =>
    t(`CreateShift.${key}`),
  );
  const handleSubmitClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
    if (isLoading || !isFormValid) {
      return;
    }
    handleSubmitGigForm();
  };

  const handleRateChange = (min: number, max: number): void => {
    setRecruitFormData((previousFormData) => ({
      ...previousFormData,
      minRate: min,
      maxRate: max,
    }));
  };

  const handlePaymentTypeChange = (
    paymentType: RecruitFormData["paymentType"],
  ): void => {
    setRecruitFormData((previousFormData) => ({
      ...previousFormData,
      paymentType,
    }));
  };

  const handleDescriptionChange = (description: string): void => {
    setFormData((prev) => ({ ...prev, description }));
  };

  const submitButton = (
    <PrimaryButton
      size="small"
      iconName="v2/checkmark"
      styleOverrides={{
        backgroundColor: ColorV2.SimflexityGreen,
        hoverBackgroundColor: ColorV2.SimflexityGreen,
        activeBackgroundColor: ColorV2.SimflexityGreen,
      }}
      onClick={handleSubmitClick}
      disabled={isLoading || !isFormValid}
      label={t("CreateShift.CreateGig")}
    />
  );

  return (
    <>
      <Modal
        label={t("CreateShift.NewSchedule")}
        size="largeSlim"
        isOpen={props.showGigFormModal}
        onClose={handleRequestClose}
        footer={
          !isLoading && !isFormValid ? (
            <TooltipHover
              arrow="down"
              textAlign="left"
              content={
                <div>
                  {createDisabledReasons.map((reason) => (
                    <div key={reason}>• {reason}</div>
                  ))}
                </div>
              }
            >
              <span>{submitButton}</span>
            </TooltipHover>
          ) : (
            submitButton
          )
        }
      >
        <ModalBody aria-busy={isLoading}>
          <GigFormTabs
            canRecruit={canRecruit}
            tab={tab}
            isLoading={isLoading}
            havePublicModule={havePublicModule}
            haveTeamModule={haveTeamModule}
            visibleForValue={visibleForValue}
            requestTabChange={requestTabChange}
            setFormData={setFormData}
          />

          <FormFlow>
            <GigFormTabPanels
              tab={tab}
              canRecruit={canRecruit}
              isPublicRecruit={isPublicRecruit}
              recruitFormData={recruitFormData}
              setRecruitFormData={setRecruitFormData}
              setFormData={setFormData}
              formData={formData}
              havePublicModule={havePublicModule}
              haveTeamModule={haveTeamModule}
              selectedWorkers={selectedWorkers}
              setSelectedWorkers={setSelectedWorkers}
              handleRateChange={handleRateChange}
              handlePaymentTypeChange={handlePaymentTypeChange}
            />

            <SectionCard>
              <SectionHeaderRow>
                <Text typography="h5" color={ColorV2.Blue1}>
                  {t("CreateShift.Shifts")}
                </Text>
                <SectionMetrics>
                  <Text typography="h5" color={ColorV2.Blue4}>
                    {t("CreateShift.ShiftsCount", {
                      count: formData.shifts.length,
                      defaultValue: "{{count}} shifts",
                    })}
                  </Text>
                  <Text typography="h5" color={ColorV2.Blue1}>
                    {hasInvalidShiftTimes
                      ? t("CreateShift.TotalDurationUnavailable", {
                          defaultValue: "Total tid otillganglig",
                        })
                      : t("CreateShift.TotalDuration", {
                          hours: totalHours,
                          minutes: totalMinutes,
                          defaultValue: "{{hours}}h {{minutes}}m total",
                        })}
                  </Text>
                </SectionMetrics>
              </SectionHeaderRow>
              <ShiftList
                shiftListOpen={shiftListOpen}
                shifts={formData.shifts}
                overlapItems={overlapping}
                setFormData={setFormData}
              />
              {!props.shifts &&
                (formData.shifts.length === 0 || isAddingMoreShifts) && (
                  <ShiftForm
                    setFormData={setFormData}
                    showCancelButton={formData.shifts.length > 0}
                    onCancelAdd={() => setIsAddingMoreShifts(false)}
                    onShiftCreated={() => setIsAddingMoreShifts(false)}
                  />
                )}
              {!props.shifts &&
                formData.shifts.length > 0 &&
                !isAddingMoreShifts && (
                  <AddMoreShiftsRow>
                    <SecondaryButton
                      label={t("CreateShift.AddMoreShifts")}
                      size="small"
                      iconName="v2/plus"
                      onClick={() => setIsAddingMoreShifts(true)}
                    />
                  </AddMoreShiftsRow>
                )}
            </SectionCard>

            {tab === "Schedule" && (
              <SectionCard>
                <Text typography="h5" color={ColorV2.Blue1}>
                  {t("CreateShift.Description")}
                </Text>
                <DescriptionFieldWrap>
                  <TextArea
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder={t("CreateShift.DescriptionInfo")}
                    maxLength={GIG_DESCRIPTION_MAX_LENGTH}
                    rows={1}
                    disabled={isLoading}
                  />
                </DescriptionFieldWrap>
              </SectionCard>
            )}
          </FormFlow>
        </ModalBody>
      </Modal>

      {showChangeTabAlert && (
        <Modal
          label={t("CreateShift.ShowChangeTabAlertTitle")}
          size="compact"
          showCloseButton={false}
          isOpen={showChangeTabAlert}
          onClose={() => setShowChangeTabAlert(false)}
          footer={
            <>
              <SecondaryButton
                label={t("OrganizationalStructure.Cancel")}
                onClick={() => setShowChangeTabAlert(false)}
              />
              <PrimaryButton
                label={t("OrganizationalStructure.Confirm")}
                onClick={() => handleChangeTab(changeTo)}
              />
            </>
          }
        >
          <Text typography="b1" color={ColorV2.Blue3}>
            {t("CreateShift.ShowChangeTabAlertInfo")}
          </Text>
        </Modal>
      )}

      {showAlert && (
        <Modal
          variant="confirm"
          label={t("OrganizationalStructure.WorkerAlertTitle")}
          size="compact"
          showCloseButton={false}
          isOpen={showAlert}
          onClose={() => setShowAlert(false)}
          footer={
            <>
              <SecondaryButton
                label={t("OrganizationalStructure.Cancel")}
                onClick={() => setShowAlert(false)}
              />
              <PrimaryButton
                label={t("OrganizationalStructure.Confirm")}
                onClick={closeModal}
              />
            </>
          }
        >
          <Text typography="b1" color={ColorV2.Blue3}>
            {t("OrganizationalStructure.WorkerAlertInfo")}
          </Text>
        </Modal>
      )}
    </>
  );
};
